/**
 * Orientation of the 2×2 engraved tile artwork.
 *
 * The Studio draws an engraved tile on the grid from its catalog photo (the
 * Shopify product image), while the Studio draws its preview from the line-art
 * SVGs in `components/Shapes/SVG`. For most patterns the two agree, but the
 * Zen and Topo artwork is drawn turned in the SVG relative to the tile that
 * actually lands on the grid: Zen is a half turn off and Topo a quarter turn.
 *
 * The repo already documents this for the wainscoting thumbnail renderer,
 * which applies a 180° base-art correction for EngravedZen for exactly this
 * reason (server/utils/wainscotingDesignSync/renderThumbnailCore.mjs).
 *
 * The correction is a BASE-ART offset: it turns the artwork itself and is
 * composed with the rotation the user picks rather than replacing it, so a
 * 90° choice still places the tile at 90°.
 */
export const ENGRAVED_BASE_ART_FLIP: Record<string, number> = {
  Zen: 180,
  Topo: 90,
}

/**
 * The extra turn a pattern's artwork needs so the preview matches the tile
 * that lands on the grid. 0 means the SVG already agrees with the tile.
 */
export const engravedBaseArtFlip = (pattern: string | undefined): number => {
  if (!pattern) return 0
  return ENGRAVED_BASE_ART_FLIP[pattern] ?? 0
}

/**
 * The rotation to apply to the preview artwork for a given shape, so that what
 * the preview shows is what lands on the grid.
 */
export const engravedPreviewRotation = (
  shape: { prefix?: string; rotation?: number } | null | undefined,
  rotation: number,
): number => (rotation + engravedBaseArtFlip(shape?.prefix)) % 360
